import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Radar, Plus, Play, Pause, Loader2, Calendar, AlertTriangle,
  CheckCircle2, RefreshCw, Trash2, ExternalLink, Zap, Bell, TrendingUp, Activity,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const NAVY = "#0B1B2F";
const GOLD = "#C3A05A";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function IntelligenceWatches() {
  const [params, setParams] = useSearchParams();
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [cnj, setCnj] = useState("");
  const [watchKind, setWatchKind] = useState("cnj"); // "cnj" | "cnpj"
  const [busy, setBusy] = useState(false);
  const [subBusy, setSubBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/intel/watches");
      setWatches(r.data.items || []);
    } catch (e) {
      toast.error("Falha ao carregar watches");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sub = params.get("sub");
    const cancelled = params.get("cancelled");
    if (sub) {
      toast.success("Assinatura em processamento — a watch aparecerá aqui após confirmação");
      params.delete("sub"); setParams(params, { replace: true });
      setTimeout(load, 1500);
    }
    if (cancelled) {
      toast.info("Assinatura cancelada.");
      params.delete("cancelled"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  const activeCount = watches.filter(w => w.active).length;
  const totalAlerts = watches.reduce((a, w) => a + (w.total_alerts || 0), 0);

  const openDetail = async (w) => {
    setSelected(w.watch_id);
    setHistory(null);
    try {
      const r = await api.get(`/intel/watches/${w.watch_id}/history`);
      setHistory(r.data);
    } catch (e) {
      toast.error("Falha ao buscar histórico");
    }
  };

  const toggleActive = async (w) => {
    setBusy(w.watch_id);
    try {
      if (w.active) {
        await api.delete(`/intel/watches/${w.watch_id}`);
        toast.success("Watch pausada");
      } else {
        await api.post(`/intel/watches/${w.watch_id}/reactivate`);
        toast.success("Watch reativada");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    } finally { setBusy(null); }
  };

  const cancelSubscription = async (w) => {
    if (!w.subscription_id) return;
    if (!window.confirm("Cancelar a assinatura Stripe imediatamente? Sem prorrogar cobrança.")) return;
    setBusy(w.watch_id);
    try {
      await api.post(`/intel/watch-subscriptions/${w.subscription_id}/cancel`);
      toast.success("Assinatura cancelada — watch desativada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao cancelar");
    } finally { setBusy(null); }
  };

  const checkNow = async (w) => {
    setBusy(w.watch_id);
    try {
      const r = await api.post(`/intel/watches/${w.watch_id}/check-now`);
      const diff = r.data?.new_movements?.length || 0;
      toast.success(diff > 0
        ? `${diff} movimento(s) novo(s) detectado(s)`
        : "Nenhum movimento novo desde a última checagem");
      await load();
      if (selected === w.watch_id) await openDetail(w);
    } catch (e) {
      toast.error("Falha ao checar agora");
    } finally { setBusy(null); }
  };

  const createFree = async () => {
    if (!cnj.trim()) return;
    setBusy("__create__");
    try {
      const body = watchKind === "cnpj"
        ? { kind: "cnpj", cnpj: cnj.trim() }
        : { cnj: cnj.trim() };
      await api.post("/intel/watches", body);
      toast.success(watchKind === "cnpj"
        ? "CNPJ Watch criada (monitora situação cadastral RFB)"
        : "Watch criada (modo grátis, sem cobrança recorrente)");
      setShowCreate(false); setCnj("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar");
    } finally { setBusy(null); }
  };

  const subscribe = async () => {
    if (!cnj.trim()) return;
    if (watchKind === "cnpj") {
      toast.error("Assinatura recorrente disponível apenas para CNJ nesta fase");
      return;
    }
    setSubBusy(true);
    try {
      const r = await api.post("/intel/watch-subscriptions", { cnj: cnj.trim() });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao assinar");
    } finally { setSubBusy(false); }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="intel-watches-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/inteligencia" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <ArrowLeft className="w-3 h-3"/> Voltar
            </Link>
            <h1 className="mt-1 font-heading text-3xl font-semibold" style={{ color: NAVY }}>
              Processo Watch
            </h1>
            <p className="text-sm text-slate-500">
              Monitoramento automático de movimentos oficiais no DataJud/CNJ · alerta por email quando houver diff
            </p>
          </div>
          <button onClick={() => setShowCreate(true)}
            data-testid="btn-create-watch"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: NAVY }}>
            <Plus className="w-4 h-4"/> Nova watch
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard icon={Radar} label="Watches ativas" value={activeCount}
            color="emerald" data-testid="stat-active-watches"/>
          <StatCard icon={Bell} label="Alertas enviados" value={totalAlerts}
            color="gold" data-testid="stat-total-alerts"/>
          <StatCard icon={Activity} label="Total no histórico" value={watches.length}
            color="slate" data-testid="stat-total-watches"/>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-500"><Loader2 className="inline w-5 h-5 animate-spin"/></div>
        ) : watches.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
            Nenhuma watch ainda. Crie a primeira em <b>+ Nova watch</b>.
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-4">
            {/* Lista */}
            <div className="space-y-2" data-testid="watches-list">
              {watches.map(w => {
                const active = selected === w.watch_id;
                return (
                  <div key={w.watch_id}
                    data-testid={`watch-row-${w.watch_id}`}
                    className={`bg-white rounded-xl border p-3 transition
                      ${active ? "border-slate-800 shadow-md" : "border-slate-200 hover:border-slate-400"}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0
                        ${w.active ? "bg-emerald-50" : "bg-slate-100"}`}>
                        <Radar className={`w-4 h-4 ${w.active ? "text-emerald-600" : "text-slate-400"}`}/>
                      </div>
                      <button onClick={() => openDetail(w)} className="flex-1 min-w-0 text-left">
                        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                          {w.active ? "ATIVA" : "PAUSADA"}
                          {w.stripe_subscription_id && <Zap className="w-3 h-3 text-amber-500" title="Assinatura Stripe"/>}
                        </div>
                        <div className="font-mono text-xs text-slate-500 truncate">{w.watch_id}</div>
                        <div className="text-sm truncate" style={{ color: NAVY }}>{w.cnj_input}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {w.total_alerts || 0} alerta(s) · última checagem {fmtDate(w.last_check_at)}
                        </div>
                      </button>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => checkNow(w)}
                          disabled={busy === w.watch_id || !w.active}
                          data-testid={`btn-check-${w.watch_id}`}
                          title="Verificar agora"
                          className="p-1.5 rounded border hover:bg-slate-50 disabled:opacity-40">
                          {busy === w.watch_id ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                        </button>
                        <button onClick={() => toggleActive(w)}
                          disabled={busy === w.watch_id}
                          data-testid={`btn-toggle-${w.watch_id}`}
                          title={w.active ? "Pausar" : "Reativar"}
                          className={`p-1.5 rounded border hover:bg-slate-50
                              ${w.active ? "text-amber-600" : "text-emerald-600"}`}>
                          {w.active ? <Pause className="w-3 h-3"/> : <Play className="w-3 h-3"/>}
                        </button>
                        {w.subscription_id && (
                          <button onClick={() => cancelSubscription(w)}
                            disabled={busy === w.watch_id}
                            data-testid={`btn-cancel-sub-${w.watch_id}`}
                            title="Cancelar assinatura Stripe"
                            className="p-1.5 rounded border hover:bg-rose-50 text-rose-600">
                            <Trash2 className="w-3 h-3"/>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detalhe */}
            <div className="bg-white rounded-2xl border border-slate-200 min-h-[400px]" data-testid="watch-detail">
              {!selected && (
                <div className="p-8 text-center text-slate-500">
                  Selecione uma watch à esquerda para ver o histórico.
                </div>
              )}
              {selected && !history && (
                <div className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2"/> Carregando…
                </div>
              )}
              {selected && history && <WatchDetail history={history}/>}
            </div>
          </div>
        )}
      </div>

      {/* Modal criar watch */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => !busy && setShowCreate(false)}
             data-testid="create-watch-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
                <Radar className="w-5 h-5" style={{ color: GOLD }}/>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Nova watch</div>
                <div className="font-heading text-lg font-semibold" style={{ color: NAVY }}>
                  {watchKind === "cnpj" ? "CNPJ Watch" : "Processo Watch"}
                </div>
              </div>
            </div>

            {/* Toggle CNJ ⇄ CNPJ */}
            <div className="mb-3 flex gap-2 bg-slate-100 rounded-lg p-1" data-testid="watch-kind-toggle">
              {[
                { id: "cnj", label: "CNJ · Processo" },
                { id: "cnpj", label: "CNPJ · Empresa" },
              ].map(opt => (
                <button key={opt.id}
                  data-testid={`watch-kind-${opt.id}`}
                  onClick={() => { setWatchKind(opt.id); setCnj(""); }}
                  disabled={busy || subBusy}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-widest transition
                    ${watchKind === opt.id ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
                  {opt.label}
                </button>
              ))}
            </div>

            <label className="text-xs uppercase tracking-widest text-slate-500">
              {watchKind === "cnpj" ? "CNPJ" : "CNJ do processo"}
            </label>
            <input autoFocus value={cnj}
              onChange={(e) => setCnj(e.target.value)}
              placeholder={watchKind === "cnpj"
                ? "00.000.000/0000-00"
                : "0000000-00.0000.0.00.0000"}
              disabled={busy || subBusy}
              data-testid="create-watch-cnj"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-slate-500"/>
            <div className="mt-4 space-y-2 text-xs text-slate-600 leading-relaxed">
              {watchKind === "cnpj" ? (
                <>
                  <p><b>CNPJ Watch:</b> monitora mudanças em <b>situação cadastral RFB</b>
                    (ATIVA → INAPTA → SUSPENSA → BAIXADA), capital, sócios e CNAE.</p>
                  <p><b>Modo grátis:</b> checagem diária às 6h. Sem assinatura recorrente disponível nesta fase.</p>
                </>
              ) : (
                <>
                  <p><b>Modo grátis:</b> checagem diária às 6h, sem cobrança recorrente (encerrado a qualquer momento).</p>
                  <p><b>Assinatura mensal:</b> alerta prioritário + cobrança recorrente Stripe.</p>
                </>
              )}
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => !busy && setShowCreate(false)}
                disabled={busy}
                className="flex-1 py-2.5 rounded-lg border border-slate-300 text-sm font-medium">
                Cancelar
              </button>
              <button onClick={createFree}
                disabled={busy === "__create__" || !cnj.trim()}
                data-testid="create-free-submit"
                className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-slate-800 disabled:opacity-60"
                style={{ color: NAVY }}>
                {busy === "__create__" ? <Loader2 className="w-4 h-4 animate-spin inline"/> : "Grátis"}
              </button>
              {watchKind === "cnj" && (
                <button onClick={subscribe}
                  disabled={subBusy || !cnj.trim()}
                  data-testid="subscribe-submit"
                  className="flex-1 py-2.5 rounded-lg text-white text-sm font-medium inline-flex items-center justify-center gap-1 disabled:opacity-60"
                  style={{ background: NAVY }}>
                  {subBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <ExternalLink className="w-4 h-4"/>}
                  Assinar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "slate", ...rest }) {
  const map = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-300",
    gold: "text-amber-700 bg-amber-50 border-amber-300",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${map[color] || map.slate}`} {...rest}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4"/>
        <div className="text-[10px] uppercase tracking-widest">{label}</div>
      </div>
      <div className="text-3xl font-heading font-semibold mt-1" style={{ color: NAVY }}>{value}</div>
    </div>
  );
}

function WatchDetail({ history }) {
  const { watch, alerts, last_snapshot } = history;
  const chartData = useMemo(() => {
    // Constrói série diária dos alertas (últimos 30 dias)
    const buckets = {};
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      buckets[key] = { date: key.slice(5), alerts: 0 };
    }
    for (const a of (alerts || [])) {
      const key = (a.sent_at || "").slice(0, 10);
      if (buckets[key]) buckets[key].alerts += (a.movements_count || 1);
    }
    return Object.values(buckets);
  }, [alerts]);

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Watch</div>
          <div className="font-mono text-xs text-slate-500">{watch.watch_id}</div>
          <div className="font-heading text-xl font-semibold mt-1" style={{ color: NAVY }}>
            {watch.cnj_input}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Criada {fmtDate(watch.created_at)}
            {watch.stripe_subscription_id && watch.stripe_subscription_id !== "dev-force" && (
              <span className="ml-2 inline-flex items-center gap-0.5 text-amber-600">
                <Zap className="w-3 h-3"/> Assinatura
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <span className={`text-[10px] tracking-widest border rounded px-1.5 py-0.5
              ${watch.active ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "bg-slate-50 text-slate-500 border-slate-300"}`}>
            {watch.active ? "ATIVA" : "PAUSADA"}
          </span>
          <div className="text-2xl font-heading font-semibold mt-2" style={{ color: NAVY }}>
            {watch.total_alerts || 0}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Alertas</div>
        </div>
      </div>

      {/* Chart */}
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
        <TrendingUp className="w-3 h-3"/> Movimentos detectados (30 dias)
      </div>
      <div className="h-40 w-full" data-testid="watch-chart">
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="date" fontSize={9} stroke="#94a3b8" interval={4}/>
            <YAxis fontSize={10} stroke="#94a3b8" allowDecimals={false}/>
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}/>
            <Line type="monotone" dataKey="alerts" stroke={GOLD} strokeWidth={2} dot={{ r: 2 }}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Última snapshot */}
      {last_snapshot && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Último snapshot</div>
          <div className="text-sm text-slate-700">
            <b>{last_snapshot.snapshot_meta?.classe || "—"}</b>
            <span className="text-slate-500"> · {last_snapshot.snapshot_meta?.orgao_julgador || "—"}</span>
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-2">
            <span>Capturado {fmtDate(last_snapshot.captured_at)}</span>
            <span>·</span>
            <span className="font-mono">hash {last_snapshot.snapshot_hash?.slice(0, 12)}…</span>
          </div>
        </div>
      )}

      {/* Alertas */}
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
          <Bell className="w-3 h-3"/> Alertas enviados ({(alerts || []).length})
        </div>
        {(alerts || []).length === 0 ? (
          <div className="text-sm text-slate-500 italic">Nenhum alerta enviado ainda.</div>
        ) : (
          <ol className="space-y-2">
            {alerts.map(a => (
              <li key={a.alert_id} className="border border-slate-200 rounded-lg p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="w-3 h-3 text-amber-600"/>
                    <b>{a.movements_count} movimento(s)</b>
                  </div>
                  <span className="text-[11px] text-slate-500">{fmtDate(a.sent_at)}</span>
                </div>
                {a.email_message_id && (
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Email · {String(a.email_message_id).slice(0, 30)}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
